import { ChangeDetectionStrategy, Component, WritableSignal, signal } from '@angular/core';
import { IBackgroundTaskLog } from 'lib/background-tasks/background-task-models';
import { numberHelper } from 'lib/helpers/number-helper';
import { IBaseTableItem, ITable } from 'lib/ui/table/index.js';
import { map } from 'rxjs';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';

interface ITableLogItem extends IBaseTableItem {
	task: IBackgroundTaskLog;
}

@Component({
	templateUrl: './list-background-logs.component.html',
	selector: 'mod-list-background-logs',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ListBackkgroundLogsComponent extends TypeCoreComponent {
	public readonly table: WritableSignal<ITable<ITableLogItem> | undefined> = signal(undefined);

	constructor() {
		super();

		this.dataInit = () => {
			this.initLogs();
		};
	}

	private initLogs(): void {
		super.subscribeToObservable(
			this.dependencies.httpService
				.get<IBackgroundTaskLog[]>((builder) =>
					builder.withCustomApiRoute('admin', 'getBackgroundTaskLogs').build()
				)
				.pipe(
					map((response) => {
						this.table.set({
							itemType: 'Log',
							search: {
								type: 'local'
							},
							noItemsText: {
								noItems: `There are no logs`,
								noFilteredItems: `No logs match your filter`
							},
							items: response.data.map((m) => {
								const tableItem: ITableLogItem = {
									id: m.name,
									task: m
								};

								return tableItem;
							}),
							id: (item) => item.id,
							title: {
								title: 'Task',
								value: (item) => item.task.name
							},
							mainIcon: (item) => {
								return {
									type: item.task.isSuccess ? 'done' : 'cancel',
									color: item.task.isSuccess ? 'success' : 'error'
								};
							},
							subtitle: (item) => `${item.task.result}`,
							mainItemClick: (item) => {
								this.dependencies.dialogService.openTextDialog({
									title: item.task.name,
									text: item.task.result,
									tableItems: [
										{
											iconType: 'number',
											title: 'Invocations',
											value: `${numberHelper.formatNumber(item.task.invocations)}`,
											valueType: 'text'
										},
										{
											iconType: 'date',
											title: 'Last run',
											value: `${super.formatDateTime(item.task.lastRun)}`,
											valueType: 'text'
										},
										{
											iconType: 'date',
											title: 'Next run',
											value: `${super.formatDateTime(item.task.nextRun)}`,
											valueType: 'text'
										}
									]
								});
							},
							fields: [
								{
									format: 'text',
									title: `Last run`,
									value: (item) => this.dependencies.timeService.fromNow(item.task.lastRun),
									sortBy: (item) => new Date(item.task.lastRun),
									isDefaultSort: false,
									showOnMobiles: true
								},
								{
									format: 'text',
									title: `Next run`,
									value: (item) => this.dependencies.timeService.fromNow(item.task.nextRun),
									sortBy: (item) => new Date(item.task.nextRun),
									isDefaultSort: false,
									showOnMobiles: true
								},
								{
									format: 'tag',
									title: `Invocations`,
									tagType: (item) => 'basic',
									value: (item) => numberHelper.formatNumber(item.task.invocations),
									sortBy: (item) => item.task.invocations,
									isDefaultSort: true,
									showOnMobiles: true
								}
							]
						});
					})
				)
		);
	}
}
